import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Eye,
  EyeOff,
  Inbox,
  Loader2,
  RefreshCw,
  Search,
  Send,
  UserCog,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";

const getPermissionId = (permission) => {
  return String(
    permission?.pk_permission_id || permission?.permission_id || "",
  );
};

const getPermissionName = (permission) => {
  return (
    permission?.permission_name || permission?.permissionName || "Permission"
  );
};

const getRoleId = (role) => {
  return String(role?.pk_role_id || role?.role_id || role?.id || "");
};

const getRoleName = (role) => {
  const rawRole =
    role?.role ||
    role?.roleName ||
    role?.role_name ||
    "Unnamed Role";

  return String(rawRole).split("|")[0]?.trim() || "Unnamed Role";
};

const getAdminId = (admin) => {
  if (!admin || typeof admin !== "object") return "";

  const id =
    admin.pk_admin_id ??
    admin.admin_id ??
    admin.adminId ??
    admin.id ??
    admin.admin_loginid ??
    admin.userId;

  return id === null || id === undefined ? "" : String(id).trim();
};

const getAdminName = (admin) => {
  const firstName = admin?.first_name || admin?.firstName || "";
  const lastName = admin?.last_name || admin?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();

  return admin?.admin_username || fullName || "Unnamed Admin";
};

const getAdminLoginId = (admin) => {
  return String(admin?.admin_loginid || admin?.userId || "");
};

const getRoleNameById = (roles = [], roleId = "") => {
  const role = roles.find((item) => String(getRoleId(item)) === String(roleId));
  return role ? getRoleName(role) : "No Role";
};

const getSidebarAccessId = (item) => {
  return String(item?.id || item?.pk_sidebar_id || "");
};

const getMenuId = (item) => {
  return String(item?.pk_menu_id || item?.menuId || "");
};

const getSubmenuId = (item) => {
  return String(item?.pk_submenu_id || item?.submenuId || item?.id || "");
};

const getMenuTitle = (item) => {
  return item?.menu_title || item?.menuTitle || "Untitled Menu";
};

const getSubmenuTitle = (item) => {
  return (
    item?.submenu_title || item?.submenuTitle || item?.menu_title || "Untitled"
  );
};

const getMenuStatus = (item) => {
  return (
    item?.status === "Y" || item?.is_active === "Y" || item?.is_active === 1
  );
};

const buildAccessKey = (item) => {
  return `${getMenuId(item)}_${getSubmenuId(item)}`;
};

const buildSidebarSelectionKey = (item) => {
  if (!item || typeof item !== "object") {
    return "";
  }

  const menuId = getMenuId(item);
  const submenuId = getSubmenuId(item);
  const itemId = String(item?.id || "");
  const url = String(item?.submenu_url || item?.menu_url || "");

  return [
    menuId || "menu",
    submenuId || itemId || "submenu",
    url || "no-url",
  ].join("_");
};

const buildAccessManagementKey = (item) => {
  return `${item?.permissionId}_${item?.menuId}_${item?.submenuId}`;
};

const groupPermissionsByModule = (permissions = []) => {
  return permissions.reduce((grouped, permission) => {
    const moduleName = permission?.module_name || "Other Permissions";

    if (!grouped[moduleName]) {
      grouped[moduleName] = [];
    }

    grouped[moduleName].push(permission);
    return grouped;
  }, {});
};

const buildRolePermissionRowMap = (rolePermissions = []) => {
  const safeRolePermissions = Array.isArray(rolePermissions)
    ? rolePermissions
    : [];

  return safeRolePermissions.reduce((map, item) => {
    if (!item || typeof item !== "object") return map;

    const permissionId =
      item.permission_id === null || item.permission_id === undefined
        ? ""
        : String(item.permission_id);

    if (!permissionId) return map;

    map[permissionId] = item;
    return map;
  }, {});
};

const buildActivePermissionIdSet = (rolePermissions = []) => {
  const safeRolePermissions = Array.isArray(rolePermissions)
    ? rolePermissions
    : [];

  return new Set(
    safeRolePermissions
      .filter(
        (item) =>
          item &&
          typeof item === "object" &&
          (item.is_active === "Y" ||
            item.is_active === 1 ||
            item.is_active === "1"),
      )
      .map((item) => item?.permission_id)
      .filter((id) => id !== null && id !== undefined && id !== "")
      .map(String),
  );
};

const flattenSidebarAccess = (accessList = []) => {
  const result = [];

  accessList.forEach((menu) => {
    const submenus = Array.isArray(menu?.submenus) ? menu.submenus : [];

    if (submenus.length === 0) {
      result.push({
        id: getSidebarAccessId(menu),
        pk_menu_id: menu?.pk_menu_id,
        pk_submenu_id: menu?.pk_submenu_id,
        menu_title: menu?.menu_title,
        menu_icon: menu?.menu_icon,
        menu_url: menu?.menu_url,
        menu_type: menu?.menu_type,
        status: menu?.status,
        submenu_title: menu?.menu_title,
        submenu_icon: menu?.menu_icon,
        submenu_url: menu?.menu_url,
      });
      return;
    }

    submenus.forEach((submenu) => {
      result.push({
        id: submenu?.id || menu?.id,
        pk_menu_id: menu?.pk_menu_id,
        pk_submenu_id: submenu?.id || menu?.pk_submenu_id,
        menu_title: menu?.menu_title,
        menu_icon: menu?.menu_icon,
        menu_url: menu?.menu_url,
        menu_type: menu?.menu_type,
        status: submenu?.status || menu?.status,
        submenu_title: submenu?.submenu_title || menu?.menu_title,
        submenu_icon: submenu?.submenu_icon || menu?.menu_icon,
        submenu_url: submenu?.submenu_url || menu?.menu_url,
      });
    });
  });

  return result;
};

const buildUserAccessMap = (userAccessList = []) => {
  const flatUserAccess = flattenSidebarAccess(userAccessList);

  return flatUserAccess.reduce((map, item) => {
    const key = buildAccessKey(item);
    map[key] = item;
    return map;
  }, {});
};

const searchPermissions = (permissions = [], searchText = "") => {
  const safePermissions = Array.isArray(permissions) ? permissions : [];
  const query = normalizeSearchValue(searchText).trim();

  if (!query) return safePermissions;

  return safePermissions.filter((permission) => {
    if (!permission || typeof permission !== "object") return false;

    return [
      permission?.permission_name,
      permission?.permissionName,
      permission?.module_name,
      permission?.pk_permission_id,
      permission?.permission_id,
    ].some((value) => normalizeSearchValue(value).includes(query));
  });
};

const searchAdmins = (admins = [], searchText = "") => {
  const safeAdmins = Array.isArray(admins) ? admins : [];
  const query = normalizeSearchValue(searchText).trim();

  if (!query) return safeAdmins;

  return safeAdmins.filter((admin) => {
    if (!admin || typeof admin !== "object") {
      return false;
    }

    return [
      getAdminName(admin),
      getAdminLoginId(admin),
      getAdminId(admin),
      admin?.first_name,
      admin?.last_name,
      admin?.admin_username,
    ].some((value) => normalizeSearchValue(value).includes(query));
  });
};

const normalizeSearchValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).toLowerCase();
};

const searchSidebarAccess = (accessList = [], searchText = "") => {
  const safeList = Array.isArray(accessList) ? accessList : [];
  const query = normalizeSearchValue(searchText).trim();

  if (!query) return safeList;

  return safeList.filter((item) => {
    if (!item || typeof item !== "object") return false;

    return [
      item?.menu_title,
      item?.submenu_title,
      item?.menu_url,
      item?.submenu_url,
      item?.id,
      item?.pk_menu_id,
      item?.pk_submenu_id,
    ].some((value) => normalizeSearchValue(value).includes(query));
  });
};

const getModuleCount = (permissions = []) => {
  return Object.keys(groupPermissionsByModule(permissions)).length;
};

const InlineLoader = ({ text = "Loading...", className = "" }) => {
  return (
    <div
      className={`flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500 ${className}`}
    >
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{text}</span>
    </div>
  );
};

const EmptyState = ({
  title = "No data found",
  description = "Nothing to show here.",
}) => {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
      <div className="mb-3 rounded-full bg-white p-3 shadow-sm">
        <Inbox className="h-6 w-6 text-slate-400" />
      </div>
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <p className="mt-1 max-w-sm text-xs text-slate-500">{description}</p>
    </div>
  );
};

const AssignRoleToAdminForm = ({
  roles = [],
  admins = [],
  adminsLoading = false,
  createAdminLoading = false,
  assignRoleLoading = false,
  onCreateAdmin,
  onAssignRole,
  onRefreshAdmins,
}) => {
  const [createForm, setCreateForm] = useState({
    firstName: "",
    lastName: "",
    userId: "",
    password: "",
    roleId: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [adminSearchText, setAdminSearchText] = useState("");
  const [selectedRoleByAdmin, setSelectedRoleByAdmin] = useState({});
  const adminTableScrollRef = useRef(null);

  const safeAdmins = useMemo(() => {
    const source = Array.isArray(admins) ? admins : [];
    const uniqueAdmins = new Map();

    source.forEach((admin, index) => {
      if (!admin || typeof admin !== "object") {
        console.warn("Invalid admin object removed:", admin);
        return;
      }

      const adminId = getAdminId(admin);
      const loginId = getAdminLoginId(admin);

      const uniqueKey = adminId || loginId || `admin-fallback-${index}`;

      if (uniqueAdmins.has(uniqueKey)) {
        console.warn("Duplicate admin removed:", {
          uniqueKey,
          previous: uniqueAdmins.get(uniqueKey),
          duplicate: admin,
        });
        return;
      }

      uniqueAdmins.set(uniqueKey, {
        ...admin,
        __rowKey: uniqueKey,
        __safeAdminId: adminId,
      });
    });

    return Array.from(uniqueAdmins.values());
  }, [admins]);

  useEffect(() => {
    if (adminTableScrollRef.current) {
      adminTableScrollRef.current.scrollTop = 0;
      adminTableScrollRef.current.scrollLeft = 0;
    }
  }, [adminSearchText]);

  useEffect(() => {
    const roleMap = {};

    safeAdmins.forEach((admin) => {
      const adminId = getAdminId(admin);

      if (!adminId) {
        console.warn("Admin skipped because admin ID is missing:", admin);
        return;
      }

      roleMap[adminId] =
        admin?.fk_role_id === null || admin?.fk_role_id === undefined
          ? ""
          : String(admin.fk_role_id);
    });

    setSelectedRoleByAdmin(roleMap);
  }, [safeAdmins]);

  const filteredAdmins = useMemo(() => {
    return searchAdmins(safeAdmins, adminSearchText);
  }, [safeAdmins, adminSearchText]);

  const handleCreateFormChange = (key, value) => {
    setCreateForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCreateAdminSubmit = async (event) => {
    event.preventDefault();

    const success = await onCreateAdmin?.(createForm);

    if (success) {
      setCreateForm({
        firstName: "",
        lastName: "",
        userId: "",
        password: "",
        roleId: "",
      });
      setShowPassword(false);
    }
  };

  const handleRoleChange = (adminId, roleId) => {
    setSelectedRoleByAdmin((prev) => ({
      ...prev,
      [adminId]: roleId,
    }));
  };

  const handleAssignExistingAdminRole = async (adminId) => {
    const roleId = selectedRoleByAdmin[adminId];

    if (!roleId) {
      toast.error("Please select a role");
      return;
    }

    await onAssignRole?.(adminId, roleId);
  };

  return (
    <div className="grid h-full min-h-0 w-full grid-cols-1 gap-4 overflow-y-auto pr-1 xl:grid-cols-[minmax(320px,0.8fr)_minmax(620px,1.2fr)] xl:overflow-hidden">
      {/* ================= CREATE ADMIN LEFT PANEL ================= */}

      <div className="flex min-h-[500px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm xl:h-full xl:min-h-0">
        <div className="shrink-0 border-b border-slate-100 p-4">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-orange-50 p-2.5 text-orange-600">
              <UserPlus className="h-5 w-5" />
            </div>

            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-900">
                Create New Admin
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Create admin user and assign role directly.
              </p>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
          {roles.length === 0 ? (
            <EmptyState
              title="No roles available"
              description="Create a role first before creating an admin."
            />
          ) : (
            <form onSubmit={handleCreateAdminSubmit} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">
                  First Name
                </label>

                <input
                  type="text"
                  value={createForm.firstName}
                  onChange={(event) =>
                    handleCreateFormChange("firstName", event.target.value)
                  }
                  placeholder="Example: Tushar"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">
                  Last Name
                </label>

                <input
                  type="text"
                  value={createForm.lastName}
                  onChange={(event) =>
                    handleCreateFormChange("lastName", event.target.value)
                  }
                  placeholder="Example: Chauhan"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">
                  User ID / Login ID
                </label>

                <input
                  type="text"
                  value={createForm.userId}
                  onChange={(event) =>
                    handleCreateFormChange("userId", event.target.value)
                  }
                  placeholder="Example: 100 or mobile number"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">
                  Password
                </label>

                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={createForm.password}
                    onChange={(event) =>
                      handleCreateFormChange("password", event.target.value)
                    }
                    placeholder="Enter password"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-11 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((previous) => !previous)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">
                  Role
                </label>

                <select
                  value={createForm.roleId}
                  onChange={(event) =>
                    handleCreateFormChange("roleId", event.target.value)
                  }
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                >
                  <option value="">Select role</option>

                  {roles.map((role) => {
                    const roleId = getRoleId(role);

                    return (
                      <option key={roleId} value={roleId}>
                        {getRoleName(role)}
                      </option>
                    );
                  })}
                </select>
              </div>

              <button
                type="submit"
                disabled={createAdminLoading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <UserPlus className="h-4 w-4" />

                {createAdminLoading ? "Creating Admin..." : "Create Admin"}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* ================= ASSIGN ROLE RIGHT PANEL ================= */}

      <div className="flex min-h-[500px] min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm xl:h-full xl:min-h-0">
        <div className="flex shrink-0 flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className="shrink-0 rounded-xl bg-orange-50 p-2.5 text-orange-600">
              <UserCog className="h-5 w-5" />
            </div>

            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-900">
                Assign Role To Existing Admin
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Search admin and update their assigned role.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onRefreshAdmins}
            disabled={adminsLoading}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCw
              className={`h-4 w-4 ${adminsLoading ? "animate-spin" : ""}`}
            />
            Refresh Admins
          </button>
        </div>

        {/* Search always fixed */}
        <div className="shrink-0 border-b border-slate-100 p-4">
          <div className="relative w-full">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

            <input
              type="text"
              value={adminSearchText}
              onChange={(event) => setAdminSearchText(event.target.value)}
              placeholder="Search admin by name, login ID or admin ID..."
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              Showing{" "}
              <span className="font-bold text-slate-800">
                {filteredAdmins.length}
              </span>{" "}
              admin(s)
            </p>

            {adminSearchText ? (
              <button
                type="button"
                onClick={() => setAdminSearchText("")}
                className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200"
              >
                Clear Search
              </button>
            ) : null}
          </div>
        </div>

        {/* Only this area scrolls */}
        <div className="min-h-0 flex-1 overflow-hidden">
          {adminsLoading ? (
            <div className="h-full overflow-y-auto p-4">
              <InlineLoader text="Loading admins..." />
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className="h-full overflow-y-auto p-4">
              <EmptyState
                title="No admins found"
                description="Change the search value or refresh the admin list."
              />
            </div>
          ) : (
            <div
              ref={adminTableScrollRef}
              className="h-full w-full overflow-auto overscroll-contain"
            >
              <table className="w-full min-w-[850px] divide-y divide-slate-200 text-sm">
                <thead className="sticky top-0 z-20 bg-slate-50 shadow-sm">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      Admin
                    </th>

                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      Login ID
                    </th>

                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      Current Role
                    </th>

                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      Change Role
                    </th>

                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-slate-500">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 bg-white">
                  {filteredAdmins.map((admin, index) => {
                    const adminId = getAdminId(admin);

                    const rowKey =
                      admin?.__rowKey ||
                      adminId ||
                      getAdminLoginId(admin) ||
                      `admin-row-${index}`;

                    const currentRoleId =
                      admin?.fk_role_id === null ||
                      admin?.fk_role_id === undefined
                        ? ""
                        : String(admin.fk_role_id);

                    const selectedRoleId =
                      selectedRoleByAdmin[adminId] ?? currentRoleId;

                    const hasValidAdminId = Boolean(adminId);

                    return (
                      <tr key={rowKey} className="hover:bg-slate-50">
                        <td className="whitespace-nowrap px-4 py-3">
                          <p className="font-bold text-slate-900">
                            {getAdminName(admin)}
                          </p>

                          <p className="text-xs text-slate-400">
                            Admin ID: {adminId || "N/A"}
                          </p>
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                          {getAdminLoginId(admin) || "N/A"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700">
                            {getRoleNameById(roles, currentRoleId)}
                          </span>
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          <select
                            value={selectedRoleId}
                            disabled={!hasValidAdminId}
                            onChange={(event) =>
                              handleRoleChange(adminId, event.target.value)
                            }
                            className="w-[190px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <option value="">Select role</option>

                            {roles.map((role) => {
                              const roleId = getRoleId(role);

                              return (
                                <option key={roleId} value={roleId}>
                                  {getRoleName(role)}
                                </option>
                              );
                            })}
                          </select>
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          <button
                            type="button"
                            disabled={
                              assignRoleLoading ||
                              !hasValidAdminId ||
                              !selectedRoleId
                            }
                            onClick={() =>
                              handleAssignExistingAdminRole(adminId)
                            }
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-xs font-bold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <Send className="h-3.5 w-3.5" />
                            Assign
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-slate-100 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          Passwords are hidden for safety, even when the API returns
          admin_password.
        </div>
      </div>
    </div>
  );
};

export default function AssignRoleTab(props) {
  return (
    <div className="h-full min-h-0 overflow-hidden">
      <AssignRoleToAdminForm {...props} />
    </div>
  );
}
